import type {
    ActorEvent,
    BaseFieldConfig,
    ButtonItem,
    GalleryViewOptions,
    HighLightConditions,
    IndependentAction,
    ObjectFitConfig,
    RatioConfig,
    RecordLikeProtocol,
    RichTextContentProtocol,
    ViewField,
    ViewOptions
} from '@lighthouse/core'
import type { ReactNode } from 'react'
import React, { memo, useDeferredValue } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import { GallerySpacing } from './constants'
import { GalleryRecord } from './GalleryRecord'

const Root = styled.div`
    display: grid;
`

interface VerticalViewProps {
    id?: string
    cols: number
    headCover?: Omit<BaseFieldConfig, 'align'> & ObjectFitConfig & RatioConfig
    columns: ViewField[]
    rowStyleConditions: Record<string, HighLightConditions>
    headAvatar: GalleryViewOptions['headAvatar']
    headTitle?: BaseFieldConfig
    headTags: GalleryViewOptions['headTags']
    contentTags: GalleryViewOptions['contentTags']
    footTags: GalleryViewOptions['footTags']
    recordStyle: GalleryViewOptions['style']
    renderItem?: (record: RecordLikeProtocol) => ReactNode
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
}

export const VerticalView = memo((props: VerticalViewProps) => {
    const {
        id,
        cols,
        headCover,
        columns,
        recordStyle,
        rowStyleConditions,
        renderItem,
        contentTags,
        onRenderButtonTitle,
        onRecordClick,
        onRecordOperatorActionTrigger,
        headAvatar,
        headTags,
        headTitle,
        footTags
    } = props
    const { recordPoints } = useViewBlockContext()
    return (
        <Root id={id} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: GallerySpacing }}>
            {recordPoints.map(point => (
                <GalleryRecord
                    key={point.id}
                    id={point.id}
                    headCover={headCover}
                    columns={columns}
                    rowStyleConditions={rowStyleConditions}
                    headAvatar={headAvatar}
                    headTitle={headTitle}
                    headTags={headTags}
                    contentTags={contentTags}
                    footTags={footTags}
                    recordStyle={recordStyle}
                    renderItem={renderItem}
                    onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                    onRecordClick={onRecordClick}
                    onRenderButtonTitle={onRenderButtonTitle}
                />
            ))}
        </Root>
    )
})
